import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import type { ReactNode } from 'react';

export function Layout({ children }: { children: ReactNode }): JSX.Element {
  const { claims, signOut } = useAuth();
  const isStaff = claims?.role === 'staff' || claims?.role === 'admin';
  const [menuOpen, setMenuOpen] = useState(false);

  function closeMenu() { setMenuOpen(false); }

  return (
    <div className="min-h-screen flex flex-col bg-black">
      <header className="bg-black border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-0 flex items-center justify-between h-16">
          <Link
            to={isStaff ? '/admin/issues' : '/issues'}
            className="flex items-center gap-3 flex-shrink-0"
            onClick={closeMenu}
          >
            <img
              src="/CompanyLogo.png"
              alt="NeoSigma"
              className="w-9 h-9 rounded-lg"
            />
            <span className="text-white font-bold text-lg sm:text-xl tracking-tight">
              NeoSigma{' '}
              <span className="text-red-500 hidden sm:inline">
                {isStaff ? 'Internal' : 'Customer'} Portal
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden sm:flex items-center gap-1 sm:gap-3 text-sm">
            {claims && !isStaff && (
              <>
                <NavLink
                  to="/issues"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md font-medium transition-colors ${
                      isActive ? 'text-red-400' : 'text-gray-400 hover:text-white'
                    }`
                  }
                >
                  Issues
                </NavLink>
                <NavLink
                  to="/profile"
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md font-medium transition-colors ${
                      isActive ? 'text-red-400' : 'text-gray-400 hover:text-white'
                    }`
                  }
                >
                  Profile
                </NavLink>
              </>
            )}
            {claims && isStaff && (
              <NavLink
                to="/admin/profile"
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md font-medium transition-colors ${
                    isActive ? 'text-red-400' : 'text-gray-400 hover:text-white'
                  }`
                }
              >
                Profile
              </NavLink>
            )}
            {claims && (
              <span className="flex items-center gap-2 text-gray-500 text-xs">
                <span>{claims.email}</span>
                <span className="badge bg-gray-800 text-gray-400 border border-gray-700">
                  {claims.role}
                </span>
              </span>
            )}
            {claims && (
              <button onClick={signOut} className="btn-secondary text-xs px-3 py-1.5">
                Sign out
              </button>
            )}
          </nav>

          {/* Mobile hamburger */}
          {claims && (
            <button
              className="sm:hidden p-2 text-gray-400 hover:text-white transition-colors"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            >
              {menuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          )}
        </div>

        {/* Mobile dropdown menu */}
        {claims && menuOpen && (
          <div className="sm:hidden bg-black border-t border-gray-800">
            <div className="max-w-6xl mx-auto px-4 py-3 space-y-1">
              {!isStaff && (
                <>
                  <NavLink
                    to="/issues"
                    onClick={closeMenu}
                    className={({ isActive }) =>
                      `block px-3 py-2.5 rounded-md font-medium text-sm transition-colors ${
                        isActive ? 'text-red-400 bg-gray-900' : 'text-gray-300 hover:text-white hover:bg-gray-900'
                      }`
                    }
                  >
                    Issues
                  </NavLink>
                  <NavLink
                    to="/profile"
                    onClick={closeMenu}
                    className={({ isActive }) =>
                      `block px-3 py-2.5 rounded-md font-medium text-sm transition-colors ${
                        isActive ? 'text-red-400 bg-gray-900' : 'text-gray-300 hover:text-white hover:bg-gray-900'
                      }`
                    }
                  >
                    Profile
                  </NavLink>
                </>
              )}
              {isStaff && (
                <NavLink
                  to="/admin/profile"
                  onClick={closeMenu}
                  className={({ isActive }) =>
                    `block px-3 py-2.5 rounded-md font-medium text-sm transition-colors ${
                      isActive ? 'text-red-400 bg-gray-900' : 'text-gray-300 hover:text-white hover:bg-gray-900'
                    }`
                  }
                >
                  Profile
                </NavLink>
              )}
              <div className="px-3 py-2 text-xs text-gray-500 flex items-center gap-2">
                <span className="truncate">{claims.email}</span>
                <span className="badge bg-gray-800 text-gray-400 border border-gray-700 shrink-0">
                  {claims.role}
                </span>
              </div>
              <div className="px-3 pb-1">
                <button
                  onClick={() => { closeMenu(); signOut(); }}
                  className="btn-secondary text-sm w-full"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-8">
        {children}
      </main>

      <footer className="border-t border-gray-800 bg-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
          <div className="flex items-center gap-2">
            <img src="/CompanyLogo.png" alt="NeoSigma" className="w-5 h-5 rounded opacity-60" />
            <span>&copy; {new Date().getFullYear()} NeoSigma. All rights reserved.</span>
          </div>
          <div className="flex gap-5">
            <Link to="/privacy" className="hover:text-gray-400 transition-colors">
              Privacy Policy
            </Link>
            <Link to="/beta-disclaimer" className="hover:text-gray-400 transition-colors">
              Beta Disclaimer
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
